import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Grid3X3, List, ExternalLink, Copy, Trash2,
  Download, Clock, ImageIcon, X, SortAsc, Filter,
} from 'lucide-react';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import { useToast } from '../hooks/use-toast';
import DesignPreview from '../components/shared/DesignPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const USER_KEY = 'ivisora_user';

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
const timeAgo = (ts) => {
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const GRAD_CLASSES = [
  'from-violet-400 to-fuchsia-400',
  'from-blue-400 to-cyan-400',
  'from-amber-400 to-orange-400',
  'from-emerald-400 to-teal-400',
  'from-pink-400 to-rose-400',
  'from-indigo-400 to-purple-400',
];

/* ─── Skeleton ─────────────────────────────────────────────────────────────── */
const SkeletonCard = () => (
  <div className="rounded-2xl border border-gray-100 overflow-hidden animate-pulse bg-white">
    <div className="h-40 bg-gray-100" />
    <div className="p-3 space-y-2">
      <div className="h-3.5 bg-gray-100 rounded w-3/4" />
      <div className="h-3 bg-gray-100 rounded w-1/2" />
    </div>
  </div>
);

/* ─── Design card (grid) ──────────────────────────────────────────────────── */
const DesignCard = ({ design, onOpen, onDuplicate, onDelete, onDownload }) => {
  const [hovered, setHovered] = useState(false);
  const grad = GRAD_CLASSES[(design.id?.charCodeAt(0) || 0) % GRAD_CLASSES.length];

  return (
    <div
      className="rounded-2xl border border-gray-100 overflow-hidden bg-white cursor-pointer transition-all duration-200"
      style={{ boxShadow: hovered ? '0 8px 32px rgba(0,0,0,0.10)' : '0 1px 4px rgba(0,0,0,0.05)', transform: hovered ? 'translateY(-2px)' : 'none' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Thumbnail — same pipeline as the Templates page (full page, no crop) */}
      <DesignPreview
        design={design}
        onClick={onOpen}
        placeholder={
          <div className={`absolute inset-0 bg-gradient-to-br ${grad} flex items-center justify-center`}>
            <span className="text-white text-3xl font-black opacity-40">
              {design.name?.[0]?.toUpperCase() || 'D'}
            </span>
          </div>
        }
      >
        {/* Hover overlay */}
        {hovered && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center gap-2"
            style={{ background: 'rgba(0,0,0,0.38)', backdropFilter: 'blur(2px)' }}
          >
            <button
              onClick={e => { e.stopPropagation(); onOpen(); }}
              className="flex items-center gap-1.5 h-8 px-3 bg-white text-gray-900 rounded-lg text-xs font-semibold hover:bg-gray-100 transition-colors"
            >
              <ExternalLink className="w-3 h-3" /> Open
            </button>
            <button
              onClick={e => { e.stopPropagation(); onDuplicate(); }}
              className="h-8 w-8 bg-white/85 rounded-lg text-gray-700 flex items-center justify-center hover:bg-white transition-colors"
              title="Duplicate"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={e => { e.stopPropagation(); onDownload(); }}
              className="h-8 w-8 bg-white/85 rounded-lg text-blue-600 flex items-center justify-center hover:bg-white transition-colors"
              title="Download"
            >
              <Download className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={e => { e.stopPropagation(); onDelete(); }}
              className="h-8 w-8 bg-white/85 rounded-lg text-red-500 flex items-center justify-center hover:bg-white transition-colors"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </DesignPreview>

      {/* Info */}
      <div className="p-3">
        <p className="text-sm font-semibold text-gray-900 truncate">{design.name || 'Untitled'}</p>
        <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
          <Clock className="w-2.5 h-2.5" />
          {design.updated_at ? timeAgo(design.updated_at) : 'Recently'}
        </p>
      </div>
    </div>
  );
};

/* ─── Design row (list view) ──────────────────────────────────────────────── */
const DesignRow = ({ design, onOpen, onDuplicate, onDelete, onDownload }) => {
  const [hovered, setHovered] = useState(false);
  const grad = GRAD_CLASSES[(design.id?.charCodeAt(0) || 0) % GRAD_CLASSES.length];

  return (
    <div
      className="flex items-center gap-4 p-3 rounded-xl border border-gray-100 transition-all duration-150 bg-white"
      style={{ background: hovered ? '#faf5ff' : '#fff', borderColor: hovered ? '#ede9fe' : '#f1f5f9' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Thumbnail — same renderer as the cards, sized to a compact width */}
      <div
        className="relative w-16 rounded-lg flex-shrink-0 overflow-hidden"
        onClick={onOpen}
        style={{ cursor: 'pointer' }}
      >
        <DesignPreview
          design={design}
          placeholder={
            <div className={`absolute inset-0 bg-gradient-to-br ${grad} flex items-center justify-center`}>
              <span className="text-white text-lg font-black opacity-50">
                {design.name?.[0]?.toUpperCase() || 'D'}
              </span>
            </div>
          }
        />
      </div>

      {/* Name + time */}
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onOpen}>
        <p className="text-sm font-semibold text-gray-900 truncate">{design.name || 'Untitled'}</p>
        <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
          <Clock className="w-2.5 h-2.5" />
          {design.updated_at ? timeAgo(design.updated_at) : 'Recently'}
        </p>
      </div>

      {/* Actions */}
      <div
        className="flex items-center gap-1.5 flex-shrink-0 transition-opacity"
        style={{ opacity: hovered ? 1 : 0 }}
      >
        <button onClick={onOpen} className="h-8 px-3 rounded-lg text-xs font-semibold text-violet-600 hover:bg-violet-100 transition-colors flex items-center gap-1">
          <ExternalLink className="w-3 h-3" /> Open
        </button>
        <button onClick={onDuplicate} className="h-8 w-8 rounded-lg text-gray-500 hover:bg-gray-100 flex items-center justify-center transition-colors" title="Duplicate">
          <Copy className="w-3.5 h-3.5" />
        </button>
        <button onClick={onDownload} className="h-8 w-8 rounded-lg text-blue-500 hover:bg-blue-50 flex items-center justify-center transition-colors" title="Download">
          <Download className="w-3.5 h-3.5" />
        </button>
        <button onClick={onDelete} className="h-8 w-8 rounded-lg text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors" title="Delete">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

/* ─── Empty state ─────────────────────────────────────────────────────────── */
const EmptyState = ({ search, onCreateNew }) => (
  <div className="flex flex-col items-center justify-center py-24 text-center">
    <div className="w-20 h-20 rounded-3xl bg-violet-50 flex items-center justify-center mb-5">
      <ImageIcon className="w-10 h-10 text-violet-300" />
    </div>
    <p className="text-lg font-bold text-gray-800 mb-1">
      {search ? `No designs match "${search}"` : 'No designs yet'}
    </p>
    <p className="text-sm text-gray-400 mb-6 max-w-xs">
      {search ? 'Try a different search term.' : 'Create your first design and it will appear here.'}
    </p>
    {!search && (
      <button
        onClick={onCreateNew}
        className="flex items-center gap-2 h-10 px-5 rounded-xl text-white text-sm font-semibold transition-all hover:opacity-90 hover:-translate-y-0.5"
        style={{ background: 'linear-gradient(135deg, #7c3aed, #ec4899)', boxShadow: '0 4px 14px rgba(124,58,237,0.35)' }}
      >
        <Plus className="w-4 h-4" /> Create first design
      </button>
    )}
  </div>
);

/* ─── Delete confirmation ─────────────────────────────────────────────────── */
const DeleteConfirm = ({ name, onCancel, onConfirm }) => (
  <div
    className="fixed inset-0 z-[200] flex items-center justify-center p-4"
    style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)' }}
    onClick={onCancel}
  >
    <div
      className="w-full max-w-sm bg-white rounded-2xl p-6 shadow-2xl"
      style={{ animation: 'dc-in 0.22s cubic-bezier(0.22,1,0.36,1)' }}
      onClick={e => e.stopPropagation()}
    >
      <style>{`@keyframes dc-in{from{opacity:0;transform:scale(0.93) translateY(10px)}to{opacity:1;transform:none}}`}</style>
      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Trash2 className="w-5 h-5 text-red-600" />
      </div>
      <h3 className="text-center text-base font-bold text-gray-900 mb-1">Delete design?</h3>
      <p className="text-center text-sm text-gray-500 mb-6">
        "<span className="font-medium">{name || 'Untitled'}</span>" will be permanently deleted.
      </p>
      <div className="flex gap-3">
        <button onClick={onCancel} className="flex-1 h-10 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">Cancel</button>
        <button onClick={onConfirm} className="flex-1 h-10 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90" style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}>Delete</button>
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   UserMyDesigns
═══════════════════════════════════════════════════════════════ */
const UserMyDesigns = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('updated'); // 'updated' | 'name' | 'created'
  const [deleteTarget, setDeleteTarget] = useState(null); // design to confirm delete

  /* Auth guard + fetch */
  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const user = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
    if (!user) { navigate('/'); return; }
    fetchDesigns(token);
  }, []);

  const fetchDesigns = async (token) => {
    setLoading(true);
    try {
      if (API_URL && token) {
        const res = await fetch(`${API_URL}/api/designs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setDesigns(Array.isArray(data) ? data : data.designs || []);
        }
      }
    } catch { /* empty state shown */ }
    finally { setLoading(false); }
  };

  const handleDelete = useCallback(async (id) => {
    setDesigns(prev => prev.filter(d => d.id !== id));
    setDeleteTarget(null);
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (API_URL && token) {
      try {
        await fetch(`${API_URL}/api/designs/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* ignore */ }
    }
    toast({ title: 'Deleted', description: 'Design deleted successfully.' });
  }, [toast]);

  const handleDuplicate = useCallback(async (design) => {
    // Persist the copy so it survives refresh and opens correctly — a
    // client-only copy with a fake id would 404 in the editor. The copy
    // inherits the source thumbnail immediately; a later save refreshes it.
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!API_URL || !token) {
      toast({ title: 'Sign in required', description: 'Sign in to duplicate designs.', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/designs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: `${design.name || 'Untitled'} (copy)`,
          canvas_size: design.canvas_size || { width: 1080, height: 1080 },
          pages: design.pages || [],
          current_page_id: design.current_page_id || null,
          thumbnail: design.thumbnail || null,
          thumbnail_url: design.thumbnail_url || null,
          source_template_id: design.source_template_id || null,
          session_type: design.session_type || null,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const created = await res.json();
      setDesigns(prev => [created, ...prev]);
      toast({ title: 'Duplicated', description: `"${design.name || 'Untitled'}" has been duplicated.` });
    } catch {
      toast({ title: 'Duplicate failed', description: 'Could not duplicate this design.', variant: 'destructive' });
    }
  }, [toast]);

  const handleDownload = useCallback((design) => {
    const blob = new Blob([JSON.stringify(design, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(design.name || 'design').replace(/\s+/g, '-').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Downloaded', description: `"${design.name || 'Untitled'}" exported.` });
  }, [toast]);

  /* Filter + sort */
  const filtered = designs
    .filter(d => (d.name || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'created') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
    });

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-1">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-8">

          {/* Page header */}
          <div className="flex items-start justify-between flex-wrap gap-4 mb-7">
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight">My Designs</h1>
              <p className="text-sm text-gray-500 mt-1">
                {loading ? 'Loading…' : `${designs.length} design${designs.length !== 1 ? 's' : ''}`}
              </p>
            </div>
            <button
              onClick={() => navigate('/editor')}
              className="flex items-center gap-2 h-10 px-5 rounded-xl text-white text-sm font-semibold transition-all hover:opacity-90 hover:-translate-y-0.5 shadow-md"
              style={{ background: 'linear-gradient(135deg, #7c3aed, #ec4899)', boxShadow: '0 4px 14px rgba(124,58,237,0.35)' }}
            >
              <Plus className="w-4 h-4" /> New Design
            </button>
          </div>

          {/* Controls bar */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search designs…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-9 h-10 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <X className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 ml-auto">
              {/* Sort */}
              <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-xl px-3 h-10">
                <SortAsc className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  className="bg-transparent text-sm text-gray-700 font-medium outline-none cursor-pointer pr-1"
                >
                  <option value="updated">Last edited</option>
                  <option value="created">Date created</option>
                  <option value="name">Name A–Z</option>
                </select>
              </div>

              {/* View toggle */}
              <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className="p-1.5 rounded-lg transition-all"
                  style={{ background: viewMode === 'grid' ? '#f5f3ff' : 'transparent', color: viewMode === 'grid' ? '#7c3aed' : '#9ca3af' }}
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className="p-1.5 rounded-lg transition-all"
                  style={{ background: viewMode === 'list' ? '#f5f3ff' : 'transparent', color: viewMode === 'list' ? '#7c3aed' : '#9ca3af' }}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {[...Array(10)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState search={search} onCreateNew={() => navigate('/editor')} />
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filtered.map(d => (
                <DesignCard
                  key={d.id}
                  design={d}
                  onOpen={() => navigate(`/editor/${d.id}`)}
                  onDuplicate={() => handleDuplicate(d)}
                  onDelete={() => setDeleteTarget(d)}
                  onDownload={() => handleDownload(d)}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {filtered.map(d => (
                <DesignRow
                  key={d.id}
                  design={d}
                  onOpen={() => navigate(`/editor/${d.id}`)}
                  onDuplicate={() => handleDuplicate(d)}
                  onDelete={() => setDeleteTarget(d)}
                  onDownload={() => handleDownload(d)}
                />
              ))}
            </div>
          )}

        </div>
      </main>

      <Footer />

      {/* Delete confirmation */}
      {deleteTarget && (
        <DeleteConfirm
          name={deleteTarget.name}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => handleDelete(deleteTarget.id)}
        />
      )}
    </div>
  );
};

export default UserMyDesigns;
